import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const WHATSAPP_NUMBER = '35795989569';

const Footer = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  
  if (location.pathname.startsWith('/admin')) return null;

  const openWhatsApp = () => {
    const message = language === 'ru' 
      ? 'Здравствуйте! Меня интересуют виллы.'
      : 'Hello! I am interested in villas.';
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <footer className="bg-night text-text-inverse py-16 md:py-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
          {/* Logo & Description */}
          <div className="md:col-span-1">
            <Link to="/" className="flex flex-col items-start">
              <img 
                src="/logo-villa.png" 
                alt="Villa.ooo" 
                className="h-20 object-contain invert"
              />
              <span className="text-[9px] tracking-[0.3em] uppercase text-blue-400 -mt-3">
                Luxury Seafront
              </span>
            </Link>
            <p className="mt-4 text-text-inverse/60 text-sm leading-relaxed">
              {language === 'ru' 
                ? 'Премиальный застройщик вилл на первой линии моря в Хорватии.'
                : 'Premium seafront villa developer in Croatia.'
              }
            </p>
          </div>

          {/* Navigation */}
          <div className="md:col-span-1">
            <h4 className="label-style mb-6">{t('nav.home')}</h4>
            <nav className="flex flex-col gap-3">
              <Link 
                to="/" 
                className="text-text-inverse/60 hover:text-gold transition-colors text-sm"
                data-testid="footer-link-home"
              >
                {t('nav.home')}
              </Link>
              <Link 
                to="/catalog" 
                className="text-text-inverse/60 hover:text-gold transition-colors text-sm"
                data-testid="footer-link-catalog"
              >
                {t('nav.catalog')}
              </Link>
              <Link 
                to="/contact" 
                className="text-text-inverse/60 hover:text-gold transition-colors text-sm"
                data-testid="footer-link-contact"
              >
                {t('nav.contact')}
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div className="md:col-span-1">
            <h4 className="label-style mb-6">{t('contact.label')}</h4>
            <div className="flex flex-col gap-4 text-sm">
              <button 
                onClick={openWhatsApp}
                className="text-text-inverse/60 hover:text-[#25D366] transition-colors flex items-center gap-2 text-left"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </button>
              <a 
                href="mailto:bm@villa.ooo" 
                className="text-text-inverse/60 hover:text-gold transition-colors flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                bm@villa.ooo
              </a>
            </div>
          </div>

          {/* Location */}
          <div className="md:col-span-1">
            <h4 className="label-style mb-6">{language === 'ru' ? 'Регион' : 'Region'}</h4>
            <div className="flex items-start gap-2 text-text-inverse/60 text-sm">
              <MapPin className="w-4 h-4 mt-0.5" />
              <span>Dalmatia, Croatia</span>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/10">
          {/* SEO Text */}
          <p className="text-text-inverse/30 text-[10px] text-center mb-6 max-w-3xl mx-auto">
            {t('footer.seoText')}
          </p>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-text-inverse/40 text-xs">
                © {new Date().getFullYear()} Villa.ooo. {t('footer.rights')}
              </p>
              <p className="text-text-inverse/30 text-[10px] mt-1">
                A project of J.S. Capital Investment Group
              </p>
            </div>
            <Link 
              to="/privacy" 
              className="text-text-inverse/40 hover:text-gold transition-colors text-xs"
              data-testid="footer-privacy"
            >
              {t('footer.privacy')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
